import clsx from 'clsx'

import { useEffect, useState, useMemo } from 'preact/hooks'
import { formatMoney, globalState } from 'helpers/utils'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import useCartGlobal, { CartConfigKeys } from 'hooks/useCartGlobal'
import useURL from 'hooks/useUrl'

import { createPortal } from 'preact/compat'
import PreactImage from 'snippets/image/image'
import Icon from 'snippets/icon/icon'
import { usePromolines } from '@/components/hooks/usePromolines'
import { Promoline } from 'libs/searchspring/components/Promoline'
import { hasB2BDiscount } from '@/components/custom-elements/b2b-price'

const actionKeys = {
  PLUS: 'plus',
  MINUS: 'minus',
  REMOVE: 'remove'
}

const onChangeActionValue = (key, quantity) => {
  return {
    [actionKeys.PLUS]: quantity + 1,
    [actionKeys.MINUS]: quantity - 1,
    [actionKeys.REMOVE]: 0
  }[key]
}

const OnChangeTrigger = ({ type, value, className, p }) => {
  return (
    <div className={`group shrink-0 relative cursor-pointer ${p}`}>
      <input
        type="checkbox"
        data-action={type}
        className=" absolute inset-tl size-full cursor-pointer appearance-none border-0 bg-transparent opacity-0 z-2"
        aria-label={value}
      />
      <div className={className}>{value}</div>
    </div>
  )
}

/**
 *
 * Handles line item quantity update.
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemQuantity = ({ item, handleSetCartConfig }) => {
  const plusIcon = <Icon id="plus" />
  const minusIcon = <Icon id="minus" />
  const trashIcon = <Icon id="trash" />
  const [error, setError] = useState(null)

  /** Checks if the current Line Item new quantity is above the available inventory  */
  const handleLineItemQuantityChange = (item, newQuantity) => {
    if (newQuantity > item.inventory_quantity) {
      setError('Maximum quantity reached')
      setTimeout(() => setError(null), 5000)
      throw new Error('Unavailable product stock quantity')
    }
    setError(null)
  }

  const onLineItemQuantityChange = async (e) => {
    try {
      e.preventDefault()
      /** Get Action Key */
      const action = e.target.dataset.action
      /** Set MiniCart state to isLoading true */
      handleSetCartConfig(CartConfigKeys.isLoading, true)
      /** Get new line item quantity based off action */
      const newQuantity = onChangeActionValue(action, item.quantity)
      /** Throw an error is the new quantity is over the available stock */
      handleLineItemQuantityChange(item, newQuantity)
      /** Update line item quantity */
      await window.Cart.updateLineItemQuantity(
        {
          id: item.variant_id,
          quantity: newQuantity
        },
        { silent: false }
      )

      /** Poll Shopify for new Cart item and set cart config state */
      await window.Cart.getCart()
      handleSetCartConfig(CartConfigKeys.cart, window.Cart.value)
      handleSetCartConfig(CartConfigKeys.isLoading, false)
    } catch (error) {
      globalEmitter.emit(emitterProps.notice.global, {
        type: 'error',
        content: error.message
      })
      handleSetCartConfig(CartConfigKeys.isLoading, false)
    }
  }

  return (
    <div className="flex flex-col">
      <form onChange={onLineItemQuantityChange} className="flex items-center gap-x-0.5 w-fit">
        <OnChangeTrigger
          p="shrink-0 btn btn-primary btn-primary-alt btn-border btn-square w-[36px]"
          type={actionKeys.REMOVE}
          value={trashIcon}
          className={'size-fit children:w-[16.5px] children:h-[16.25px]'}
        />

        <div className="shrink-0 flex border border-neutral-medium rounded-80">
          <OnChangeTrigger
            p="btn btn-primary btn-transparent btn-square w-[36px] !border-0"
            type={actionKeys.MINUS}
            value={minusIcon}
            className={'size-fit children:w-[11px] children:h-[1px]'}
          />
          <div className="grow flex items-center justify-center text-s w-2 text-center">{item.quantity}</div>
          <OnChangeTrigger
            p='btn btn-primary btn-transparent btn-square w-[36px] !border-0'
            type={actionKeys.PLUS}
            value={plusIcon}
            className={'size-fit children:size-[11px]'}
          />
        </div>
      </form>
      <p className={clsx('hidden w-full paragraph-xs text-system-error mt-0.25 transition-all', error && '!flex')}>{error}</p>
    </div>
  )
}

/**
 *
 *
 * Mini Cart Snippet,
 * Currently hardcoded to handle the free shipping discount range.
 *
 * @param {*} param0
 * @returns
 */
function MiniCartSnippet({ cartSubTotal, freeShippingGoal }) {
  const handleFreeShippingValue = (amount) => {
    if (typeof amount === 'number' && amount > 0) {
      return amount
    }
    return parseInt(amount)
  }

  const freeShippingMinimum = handleFreeShippingValue(freeShippingGoal) ?? 10000
  const reachedGoal = freeShippingMinimum - cartSubTotal
  const message = reachedGoal <= 0 ? (
    <>
      <div className="shrink-0 flex w-[12px] h-[9px] children:size-full">
        <Icon id={'tick'} />
      </div>
      <p className="paragraph-s font-semibold">You're receiving free shipping on this order</p>
    </>
  ) : (
    <>
      <p className="paragraph-s"><span className="font-semibold">{formatMoney(reachedGoal)} away</span> from free shipping</p>
    </>
  )
  const progressBarPercentage = Math.round((cartSubTotal / freeShippingGoal) * 100)
  const progressBarPercentageWidth = progressBarPercentage > 100 ? '100' : progressBarPercentage
  return (
    <div data-role="free_shipping_monitor" className="flex flex-col gap-0.375 px-1 md:px-1.5 py-1.125 border-b border-neutral-medium">
      <div className={`flex items-center gap-x-0.375 transition-all ${reachedGoal <= 0 ? 'text-system-success' : ''}`}>
        {message}
      </div>
      <div className="relative w-full h-[4px] bg-neutral-light">
        <div
          className={`absolute inset-tl w-shipping-bar-width h-full transition-all ${reachedGoal <= 0 ? 'bg-system-success' : 'bg-system-warning'}`}
          style={{ '--shipping-bar-width': progressBarPercentageWidth + '%' }}
        ></div>
      </div>
    </div>
  )
}

/**
 * B2B Line Price Component
 * Renders a b2b-price element directly in JSX
 */
const B2BLinePrice = ({ item }) => {
  return (
    <b2b-price className="group/price flex flex-col items-end text-right" data-variant-id={item.variant_id} data-quantity={item.quantity}>
      <span data-original-price className="hidden group-data-[sale=true]/price:block paragraph-m line-through"></span>
      <span data-b2b-price className="paragraph-m group-data-[sale=true]/price:text-brand-sale"></span>
    </b2b-price>
  )
}

/**
 *
 * Display line item price
 * Handles different price if discount is applied
 *
 * Props Cart Line Item
 *
 * @param {*} param0
 * @returns
 */
const ItemPrice = ({ item }) => {
  const SalePrice = ({ salePrice, originalPrice }) => {
    return (
      <div className="shrink-0 flex md-max:flex-col md:justify-end gap-x-0.5 text-right">
        <span className="paragraph-s md:paragraph-m font-semibold text-brand-sale">{formatMoney(salePrice)}</span>
        <span className="paragraph-s md:paragraph-m font-semibold line-through">{formatMoney(originalPrice)}</span>
      </div>
    )
  }

  // For B2B customers, calculate line total from unit price
  const isB2BCustomer = window.globals?.customer?.b2b

  if (isB2BCustomer && item.variant_id) {
    return <B2BLinePrice item={item} />
  }

  // Priority order for non-B2B: Discounts -> Compare At -> Regular
  if (item.discounts.length > 0 && item.original_line_price !== item.line_price) {
    return <SalePrice salePrice={item.line_price} originalPrice={item.original_line_price} />
  } else if (item.compare_at_price > item.original_price) {
    return <SalePrice salePrice={item.line_price} originalPrice={item.compare_at_price * item.quantity} />
  }

  return (
    <div className="flex flex-col items-end text-right">
      <span className="paragraph-s md:paragraph-m font-semibold">{formatMoney(item.original_line_price)}</span>
    </div>
  )
}

const MiniCartItemDiscount = ({ item }) => {
  return (
    <>
      {item.discounts && item.discounts.length > 0 && (
        <ul className="list-none">
          {item.discounts.map((discount) => {
            return (
              <li className="flex gap-x-0.25 items-center paragraph-xs text-neutral-secondary list-none">
                <div className="size-[14px] children:size-full">
                  <Icon id={'tag'} />
                </div>
                {discount.title}
              </li>
            )
          })}
        </ul>
      )}
    </>
  )
}

/**
 *
 * Displays line item image
 *
 * @param {*} imageSrc
 * @param {*} id
 * @param {*} productPath
 * @returns
 */
const MiniCartItemImage = ({ imageSrc, id, productPath }) => {
  return (
    <a href={productPath} className="col-[1/2] row-[1/3] flex size-fit link-fade" aria-label="Item image">
      <PreactImage className="w-[86px] md:w-[98px] aspect-4/5 children:size-full children:object-cover" key={id} imageUrl={[imageSrc]} />
    </a>
  )
}

/**
 * Checks if item has universal sizing by looking for "Size" property
 * @param {Object} properties - Item properties object
 * @returns {boolean} - True if universal sizing exists
 */
const hasUniversalSizing = (properties) => {
  return properties && Object.prototype.hasOwnProperty.call(properties, 'Size')
}

/**
 * Formats size value based on gender and size array
 * @param {string} optionValue - The option value string
 * @param {string} gender - Product gender (Womens/Mens)
 * @returns {string} - Formatted size string or original value
 */
const formatSizeValue = (optionValue, gender) => {
  if (!optionValue) return optionValue

  const isHyphenFormat = optionValue.includes('-')
  const isPipeFormat = optionValue.includes('|')

  if (isHyphenFormat) {
    const regions = optionValue.split('-').map(r => r.trim()).filter(r => r)
    let ukSizeValue = ''
    let usSizeValue = ''
    let euSizeValue = ''

    regions.forEach(region => {
      const cleanRegion = region.trim()

      // Parse UK region: "UK M 3/W 4"
      if (cleanRegion.startsWith('UK ')) {
        const ukContent = cleanRegion.replace('UK ', '').trim()
        const parts = ukContent.split('/').map(p => p.trim())

        parts.forEach(part => {
          if (gender === 'Womens' && part.startsWith('W ')) {
            ukSizeValue = part.replace('W ', '').trim()
          } else if (gender === 'Mens' && part.startsWith('M ')) {
            ukSizeValue = part.replace('M ', '').trim()
          } else if (!gender && part.startsWith('W ')) {
            ukSizeValue = part.replace('W ', '').trim()
          } else if (!gender && part.startsWith('M ')) {
            ukSizeValue = part.replace('M ', '').trim()
          }
        })
      } else if (cleanRegion.startsWith('US ')) {
        // Parse US region: "US M 4/W 6"
        const usContent = cleanRegion.replace('US ', '').trim()
        const parts = usContent.split('/').map(p => p.trim())

        parts.forEach(part => {
          if (gender === 'Womens' && part.startsWith('W ')) {
            usSizeValue = part.replace('W ', '').trim()
          } else if (gender === 'Mens' && part.startsWith('M ')) {
            usSizeValue = part.replace('M ', '').trim()
          } else if (!gender && part.startsWith('W ')) {
            usSizeValue = part.replace('W ', '').trim()
          } else if (!gender && part.startsWith('M ')) {
            usSizeValue = part.replace('M ', '').trim()
          }
        })
      } else if (cleanRegion.startsWith('EURO ')) {
        // Parse EURO region: "EURO 37"
        euSizeValue = cleanRegion.replace('EURO ', '').trim()
      }
    })

    return `${usSizeValue} / ${ukSizeValue} / ${euSizeValue}`.trim()
  }

  if (isPipeFormat) {
    const regions = optionValue.split('|').map(r => r.trim()).filter(r => r)
    let ukSizeValue = ''
    let usSizeValue = ''
    let euSizeValue = ''

    regions.forEach(region => {
      const cleanRegion = region.trim()

      if (cleanRegion.startsWith('UK ')) {
        const ukContent = cleanRegion.replace('UK ', '').trim()
        const parts = ukContent.split('/').map(p => p.trim())

        parts.forEach(part => {
          if (gender === 'Womens' && part.startsWith('W ')) {
            ukSizeValue = part.replace('W ', '').trim()
          } else if (gender === 'Mens' && part.startsWith('M ')) {
            ukSizeValue = part.replace('M ', '').trim()
          } else if (!gender && part.startsWith('W ')) {
            ukSizeValue = part.replace('W ', '').trim()
          } else if (!gender && part.startsWith('M ')) {
            ukSizeValue = part.replace('M ', '').trim()
          }
        })
      } else if (cleanRegion.startsWith('US ')) {
        const usContent = cleanRegion.replace('US ', '').trim()
        const parts = usContent.split('/').map(p => p.trim())

        parts.forEach(part => {
          if (gender === 'Womens' && part.startsWith('W ')) {
            usSizeValue = part.replace('W ', '').trim()
          } else if (gender === 'Mens' && part.startsWith('M ')) {
            usSizeValue = part.replace('M ', '').trim()
          } else if (!gender && part.startsWith('W ')) {
            usSizeValue = part.replace('W ', '').trim()
          } else if (!gender && part.startsWith('M ')) {
            usSizeValue = part.replace('M ', '').trim()
          }
        })
      } else if (cleanRegion.startsWith('EURO ')) {
        euSizeValue = cleanRegion.replace('EURO ', '').trim()
      }
    })

    return `${usSizeValue} / ${ukSizeValue} / ${euSizeValue}`.trim()
  }

  // Fallback to original comma-separated format
  const itemSizesArray = optionValue.split(',')

  if (itemSizesArray.length <= 1) {
    return optionValue
  }

  let ukSizeValue = ''
  let usSizeValue = ''

  if (gender === 'Womens') {
    ukSizeValue = itemSizesArray[4] ? itemSizesArray[4].replace('WOMENS', '') : ''
    usSizeValue = itemSizesArray[5] ? itemSizesArray[5].replace('WOMENS', '') : ''
  } else if (gender === 'Mens') {
    ukSizeValue = itemSizesArray[6] ? itemSizesArray[6].replace('MENS', '') : ''
    usSizeValue = itemSizesArray[7] ? itemSizesArray[7].replace('MENS', '') : ''
  }

  const euSizeValue = itemSizesArray[3] ? itemSizesArray[3].replace('EURO', 'EU') : ''

  return `${usSizeValue} / ${ukSizeValue} / ${euSizeValue}`.trim()
}

/**
 *
 * Display the line item
 * 1) Title
 * 2) Variant Details (size, colour)
 * 3) Discount details (if applicable)
 * 4) Line Item price
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemDetails = ({ item }) => {
  const properties = item.properties
  const hasProperties = properties && Object.keys(properties).length > 0
  const productGender = item.product?.metafields?.utopia?.gender
  const metafieldTitle = item.product?.metafields?.custom?.web_friendly_title

  const filteredOptions = item.options_with_values.filter(option => {
    if (option.name === 'Size' && hasUniversalSizing(properties)) {
      return false
    }
    return true
  })

  return (
    <div className="flex justify-between gap-x-0.75 w-full">
      <div className="flex flex-col gap-y-0.375 md:gap-y-0.625">
        <h3 className="paragraph-b md:paragraph-m font-semibold uppercase">
          {metafieldTitle || item.product_title}
        </h3>
        <div className="flex flex-col gap-y-0.25 md:gap-y-0.5">
          {(item.colour_name || filteredOptions.length > 0 || hasProperties) && (
            <span className="paragraph-s md:paragraph-b text-neutral-secondary">
              {[
                item.colour_name,
                ...filteredOptions.map(option =>
                  option.name === 'Size'
                    ? formatSizeValue(option.value, productGender)
                    : option.value
                ),
                ...(hasProperties && properties ? Object.keys(properties)
                  .filter(property => properties[property] && !property.startsWith('_'))
                  .map(property => properties[property]) : [])
              ].filter(Boolean).join(' | ')}
            </span>
          )}
          <MiniCartItemDiscount item={item} />
        </div>
      </div>
      <ItemPrice item={item} />
    </div>
  )
}

const MiniCartLineItem = ({ item, handleSetCartConfig, isLoading }) => {
  const loadingState = isLoading && 'opacity-50'

  const criteria = useMemo(() => ({
    tags: item.tags ?? [],
    collections: item.collections?.map(c => c.handle) ?? [],
    products: [item.handle]
  }), [item.tags, item.collections, item.handle])

  const allPromolines = usePromolines(criteria)

  const isB2BCustomer = window.globals?.customer?.b2b
  const b2bPricing = window.globals?.customer?.b2bPricing
  const productId = item.uid || item.id?.toString?.() || item.id
  const b2bData = isB2BCustomer && b2bPricing ? b2bPricing[productId] : null
  const hasQuickB2bCheck = isB2BCustomer && b2bData && b2bData.price
  const [b2bPriceCheck, setB2bPriceCheck] = useState(hasQuickB2bCheck)

  useEffect(() => {
    if (isB2BCustomer && item.variant_id) {
      hasB2BDiscount(item.variant_id).then(hasDiscount => {
        setB2bPriceCheck(hasDiscount)
      }).catch(() => {
        setB2bPriceCheck(false)
      })
    } else {
      setB2bPriceCheck(false)
    }
  }, [isB2BCustomer, item.variant_id])

  const getObjects = (items) => {
    const exclusiveItem = items.find(item => item.exclusive)
    if (exclusiveItem) {
      return [exclusiveItem, null]
    }
    return [items[0] || null, items[1] || null]
  }

  const [firstPromoline, secondPromoline] = useMemo(() => getObjects(allPromolines), [allPromolines])

  return (
    <div className={`grid grid-cols-min-auto-auto gap-x-0.75 md:gap-x-1.125 gap-y-0.75 md:gap-y-1 items-center pb-1 last:pb-0 border-b last:border-b-0 border-neutral-light transition-all ${loadingState}`}>
      <MiniCartItemImage imageSrc={item.featured_image.url} id={item.id} productPath={item.url} />
      <div className="col-[2/4] row-[1]">
        <MiniCartItemDetails item={item} />
      </div>
      <div className="col-[2/3] row-[2]">
        <MiniCartItemQuantity item={item} handleSetCartConfig={handleSetCartConfig} />
      </div>
      {(b2bPriceCheck || firstPromoline || secondPromoline) && (
        <div className="col-span-full md:col-[3/4] md:row-[2] flex flex-col gap-0.25 md:items-end">
          {b2bPriceCheck && <Promoline promoline={{
            title: 'Wholesale Price',
            text_colour: '#9C0000',
            background_colour: '#FCEBEB',
            promolineSize: 'medium'
          }} promolineSize="medium" />}
          {firstPromoline && <Promoline promoline={firstPromoline} promolineLocation="Minicart" promolineSize="medium" />}
          {!b2bPriceCheck && secondPromoline && <Promoline promoline={secondPromoline} promolineLocation="Minicart" promolineSize="medium" />}
        </div>
      )}
    </div>
  )
}

/**
 * Calculates total savings from compare_at_price discounts
 * @param {Array} cartItems - Array of cart items
 * @returns {number} - Total compare_at_price savings in cents
 */
const calculateCompareAtSavings = (cartItems) => {
  let compareAtSavings = 0

  for (const item of cartItems) {
    const linePricePerItem = item.line_price / item.quantity
    const compareAtPrice = item.compare_at_price

    if (compareAtPrice && compareAtPrice > linePricePerItem) {
      const savingsPerItem = compareAtPrice - linePricePerItem
      const itemSavings = savingsPerItem * item.quantity
      compareAtSavings += itemSavings
    }
  }

  return compareAtSavings
}

/**
 *
 * Displays cart total for Mini Cart.
 *
 * @param {*} param0
 * @returns
 */
const DisplayCartTotal = ({ total, totalCartDiscount, isLoading, cart, discountText, totalText }) => {
  const cartTotal = formatMoney(total)
  const compareAtSavings = calculateCompareAtSavings(cart.items)
  const totalDiscountAmount = totalCartDiscount + compareAtSavings
  const totalDiscount = formatMoney(totalDiscountAmount)
  const subtotal = formatMoney(total + totalDiscountAmount)
  const totalItems = cart.items.reduce((sum, item) => sum + item.quantity, 0)

  return (
    <div className="flex flex-col gap-y-0.25">
      <div className="flex justify-between gap-x-1">
        <p className="paragraph-b">{totalItems} {totalItems === 1 ? 'Item' : 'Items'}</p>
        <p className={`paragraph-b transition-all ${isLoading && 'opacity-50'}`}>{subtotal}</p>
      </div>
      {totalDiscountAmount > 0 && (
        <div className="flex justify-between gap-x-1">
          <p className="paragraph-b">{discountText || 'Discounts'}</p>
          <p className={`paragraph-b text-system-success transition-all ${isLoading && 'opacity-50'}`}>-{totalDiscount}</p>
        </div>
      )}
      <div className="flex justify-between gap-x-1">
        <p className="paragraph-m font-semibold uppercase">{totalText || 'Total'}</p>
        <p className={`paragraph-m font-semibold transition-all ${isLoading && 'opacity-50'}`}>{cartTotal}</p>
      </div>
    </div>
  )
}

/**
 *
 * Empty Cart state. Renders when the customers cart is empty
 *
 * @param {*} param0
 * @returns
 */
const EmptyCart = ({ isLoading }) => {
  const buttons = window.minicart?.buttons || ''
  return (
    <>
      <div
        class={`self-center flex flex-row flex-wrap gap-0.5 items-center justify-center w-full h-fit px-1 lg:px-1.5 py-2 children:w-[calc(50%-4px)] children:max-w-[calc(50%-4px)] ${isLoading ? 'opacity-0' : ''}`}
        dangerouslySetInnerHTML={{ __html: buttons }}
      ></div>
    </>
  )
}

/**
 *
 * Cart Checkout button
 *
 * @param {*} param0
 * @returns
 */
const CheckoutButton = ({ checkoutButtonTitle }) => {
  return (
    <div>
      <a href="/checkout" className="btn btn-primary btn-fill btn-l w-full" aria-label={checkoutButtonTitle}>
        {checkoutButtonTitle}
      </a>
    </div>
  )
}

/**
 * Mini Cart Header Title
 * Shows cart title with count when items exist, or emptyDescription when empty
 */
const MiniCartHeaderTitle = ({ cart, defaultTitle, emptyDescription }) => {
  const itemCount = cart?.items?.length ? cart.item_count : 0

  if (itemCount > 0) {
    return (
      <>
        {defaultTitle}&nbsp;<span className='cart-count before:[content:"("] after:[content:")"]'>{itemCount}</span>
      </>
    )
  }

  return <>{emptyDescription || defaultTitle}</>
}

/**
 *
 * Displays the line items present in the the customers cart.
 *
 * @param {*} param0
 * @returns
 */
const CartLineItems = ({ cart, handleSetCartConfig, isLoading }) => {
  return (
    <div data-role="cart_items" className="grow flex flex-col gap-y-1 overflow-y-scroll no-scrollbar overscroll-none px-1 md:px-1.5 py-1">
      {cart.items.map((item) => {
        return <MiniCartLineItem item={{ ...item }} handleSetCartConfig={handleSetCartConfig} isLoading={isLoading} />
      })}
    </div>
  )
}

/**
 *
 * Mini Cart
 *
 * @returns
 */
export function MiniCart() {
  const path = useURL()
  const { cartConfig, handleSetCartConfig } = useCartGlobal()
  const globalCart = globalState.cart
  const { isLoading, cart } = cartConfig
  const freeShippingMinimum = globalCart?.freeShippingMinimum

  const { checkoutButtonTitle, emptyDescription, discountText, totalText } = globalCart

  const [defaultTitle, setDefaultTitle] = useState('Cart')
  const [headerReady, setHeaderReady] = useState(false)

  // Clear the Liquid content on mount so portal can take over
  useEffect(() => {
    const headerElement = document.querySelector('[data-minicart-title]')
    if (headerElement) {
      setDefaultTitle(headerElement.dataset?.defaultTitle || 'Cart')
      headerElement.innerHTML = ''
      setHeaderReady(true)
    }
  }, [])

  useEffect(() => {
    const altPaymentButtons = document.querySelector('div.additional-checkout-buttons--horizontal')
    if (!altPaymentButtons) return
    if (cart.items.length > 0) {
      altPaymentButtons.classList.remove('hidden')
    } else if (cart.items.length <= 0) {
      altPaymentButtons.classList.add('hidden')
    }
  }, [cartConfig])

  useEffect(() => {
    if (path.includes('timestamp=')) {
      handleSetCartConfig(CartConfigKeys.isLoading, true)
      window.Cart.getCart().then(() => {
        handleSetCartConfig(CartConfigKeys.cart, window.Cart.value)
        handleSetCartConfig(CartConfigKeys.isLoading, false)
      })
    }
  }, [path])

  return (
    <>
      {headerReady && (
        <MiniCartPortal targetSelector="[data-minicart-title]">
          <MiniCartHeaderTitle cart={cart} defaultTitle={defaultTitle} emptyDescription={emptyDescription} />
        </MiniCartPortal>
      )}
      {cart?.items?.length ? (
        <>
          <MiniCartPortal targetId="minicart-shipping">
            <MiniCartSnippet cartSubTotal={cart.items_subtotal_price} freeShippingGoal={freeShippingMinimum} />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-items">
            <CartLineItems cart={cart} isLoading={isLoading} handleSetCartConfig={handleSetCartConfig} />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-summary">
            <div className="flex flex-col gap-y-1 px-1 lg:px-1.5 pt-1 lg:pt-1.5 mb-1 border-t border-neutral-medium">
              <DisplayCartTotal
                totalCartDiscount={cart.total_discount}
                total={cart.total_price}
                isLoading={isLoading}
                cart={cart}
                discountText={discountText}
                totalText={totalText}
              />
              <CheckoutButton checkoutButtonTitle={checkoutButtonTitle} />
            </div>
          </MiniCartPortal>
        </>
      ) : (
        <MiniCartPortal targetId="minicart-items">
          <EmptyCart cartConfig={cartConfig} globalCart={globalCart} isLoading={isLoading} />
        </MiniCartPortal>
      )}
    </>
  )
}

const MiniCartPortal = ({ children, targetId, targetSelector }) => {
  const target = targetSelector
    ? document.querySelector(targetSelector)
    : document.getElementById(targetId)
  return target ? createPortal(children, target) : null
}
